import { type FC, type HTMLAttributes, useMemo } from "react";
import { type TypeSTCfinderSingleResult } from "~/pages/StcFinderPage/types";
import { Link } from "~/common/components/Link";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import classNames from "classnames";
import { createColumnHelper } from "@tanstack/react-table";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { useTranslation } from "react-i18next";

interface Props {
  resultsList: TypeSTCfinderSingleResult[];
}

const STCfinderTabularView: FC<Props> = (props) => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  const columnHelper = createColumnHelper<TypeSTCfinderSingleResult>();

  const columns = [
    columnHelper.accessor("name", {
      header: t("STCfinder.table.header.contact") as string,
      cell: (cell) => {
        const data = cell.row.original;
        return (
          <div className={"scha__stc-finder__results__name-cell"}>
            <p className={"m-0 mb-2"}>{data.name}</p>
            {data.phoneNumber && <PhoneNumber>{data.phoneNumber}</PhoneNumber>}
            {data.email && <EmailLink>{data.email}</EmailLink>}
          </div>
        );
      },
      size: isDesktop ? 0.42 : 220,
      enableSorting: true,
    }),
    columnHelper.accessor("street", {
      header: t("STCfinder.table.header.street") as string,
      size: isDesktop ? 0.17 : 200,
      enableSorting: true,
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("city", {
      header: t("STCfinder.table.header.city") as string,
      size: isDesktop ? 0.14 : 180,
      enableSorting: true,
    }),
    columnHelper.accessor("zipCode", {
      header: t("STCfinder.table.header.zipCode") as string,
      size: isDesktop ? 0.13 : 180,
      enableSorting: true,
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("country", {
      header: t("STCfinder.table.header.country") as string,
      size: isDesktop ? 0.13 : 180,
      enableSorting: true,
    }),
  ];

  const remodeledResultsList = useMemo(
    () =>
      props.resultsList.map((obj) => {
        const countryTranslated = t(obj.country as string);
        return { ...obj, country: countryTranslated };
      }),
    [props.resultsList],
  );

  return (
    <div>
      <TanStackTable data={remodeledResultsList} columns={columns} variant={"stripes"} fullwidth={isDesktop} />
    </div>
  );
};

const EmailLink = ({
  children,
  className,
  ...rest
}: Omit<HTMLAttributes<unknown>, "children"> & { children: string }) => {
  const isDesktop = useIsMinDesktopSize();

  return (
    <Link className={classNames("scha__email-link", className)} {...rest} href={`mailto:${children}`}>
      {!isDesktop && <Icon type={IconType.Envelope} className={"icon-margin"} />}
      {children}
    </Link>
  );
};

const PhoneNumber = ({
  children,
  className,
  ...rest
}: Omit<HTMLAttributes<unknown>, "children"> & { children: string }) => {
  const isDesktop = useIsMinDesktopSize();

  if (isDesktop) {
    return (
      <p className={classNames("scha__phone-number", className)} {...rest}>
        {children}
      </p>
    );
  }
  return (
    <Link className={classNames("scha__phone-number", className)} {...rest} href={`tel:${children}`}>
      <Icon type={IconType.Phone} className={"icon-margin"} />
      {children}
    </Link>
  );
};

export default STCfinderTabularView;
